/**
 * Publicidad (1.0 AdBanner + platform_config/ad_server): inventario vendido a anunciantes
 * (home_top / home_feed / home_bottom / product_sidebar) y configuración del ad server (proveedor
 * activo, frecuencia en el feed, ids públicos de Google AdSense y Meta Audience Network — ningún
 * secreto). `adBanners/{id}` lo escribe solo el servidor (admin-saveAdBanner); el cliente lee los
 * activos y afina fechas y país en memoria, como la 1.0.
 */
import { z } from 'zod';
import { CountryCodeSchema, TimestampSchema, UserIdSchema } from './primitives.js';

export const AD_PLACEMENTS = ['home_top', 'home_feed', 'home_bottom', 'product_sidebar'] as const;
export type AdPlacement = (typeof AD_PLACEMENTS)[number];
export const AdPlacementSchema = z.enum(AD_PLACEMENTS);

/**
 * `seguratech` (4/11, Gabor): SeguraTech Ads es el servidor de anuncios único; Nuvvu solo los pinta
 * y le manda impresiones/clics. Si SeguraTech no sirve nada (o falla / tarda), se cae a las campañas
 * propias (`adBanners`) y después a AdSense si está configurado, como hoy.
 */
export const AD_PROVIDERS = ['manual', 'google', 'meta', 'hybrid', 'seguratech'] as const;
export type AdProvider = (typeof AD_PROVIDERS)[number];

/** Servidor de SeguraTech Ads (Hosting → adsServe / adsEvent). */
export const SEGURATECH_ADS_ORIGIN = 'https://seguratech.es/api/ads';
export const SEGURATECH_ADS_PLATFORM = 'nuvvu';
export type SeguraTechAdFormat =
  'banner_site' | 'banner_home' | 'slider' | 'sponsored' | 'banner_app';
/** Hueco de Nuvvu → formato de SeguraTech. `home_bottom` no se vende en SeguraTech: solo campañas propias. */
export const SEGURATECH_FORMAT_BY_SLOT = {
  home_top: 'banner_home',
  home_feed: 'sponsored',
  product_sidebar: 'banner_site',
  home_slider: 'slider',
} as const satisfies Record<string, SeguraTechAdFormat>;
export type SeguraTechSlot = keyof typeof SEGURATECH_FORMAT_BY_SLOT;
/** El slider de SeguraTech admite hasta 4 anunciantes a la vez (cláusula 5 del contrato). */
export const SEGURATECH_SLIDER_MAX = 4;

/** Respuesta de `GET /serve` (docs/ads-sdk.md de SeguraTech). */
export const SeguraTechServedAdSchema = z.object({
  adId: z.string().regex(/^[\w-]{10,40}$/),
  format: z.string(),
  headline: z.string().max(200).catch(''),
  description: z.string().max(4000).catch(''),
  image: z.string().url().startsWith('https://').nullable().catch(null),
  images: z.array(z.string()).catch([]),
  clickUrl: z.string().max(1000).catch(''),
  /** Firma del anuncio servido (SeguraTech la comprueba en /event): se devuelve tal cual con cada evento. */
  ts: z.number().int().optional().catch(undefined),
  sig: z.string().max(64).optional().catch(undefined),
});
export type SeguraTechServedAd = z.infer<typeof SeguraTechServedAdSchema>;

/** Anuncios válidos de una respuesta de `/serve` (lo que no cuadra se descarta, nunca rompe). */
export function parseSeguraTechServe(raw: unknown): SeguraTechServedAd[] {
  const ads = (raw as { ads?: unknown } | null)?.ads;
  if (!Array.isArray(ads)) return [];
  const out: SeguraTechServedAd[] = [];
  for (const a of ads.slice(0, 10)) {
    const p = SeguraTechServedAdSchema.safeParse(a);
    if (p.success && (p.data.image || p.data.headline)) out.push(p.data);
  }
  return out;
}

/**
 * Proveedor con el que se rellena un hueco cuando SeguraTech no sirve: campaña propia y, si no la
 * hay, Google (semántica de `google` en el feed). Con cualquier otro proveedor, el mismo.
 */
export function fallbackProvider(provider: AdProvider): AdProvider {
  return provider === 'seguratech' ? 'google' : provider;
}

/** Marca de los banners que crea el servidor solo (2.1: PRO Absoluto); los manuales no la llevan. */
export const AD_AUTO_KINDS = ['pro_absolute'] as const;
/** Id fijo del banner automático de un vendedor PRO Absoluto (uno por vendedor, idempotente). */
export const proAutoBannerId = (uid: string): string => `auto_pro_${uid}`;

/** Medidas recomendadas por placement (1.0 PLACEMENT_SPECS), para el admin. */
export const AD_PLACEMENT_SPECS: Record<
  AdPlacement,
  { ratio: string; size: string; hint: string }
> = {
  home_top: { ratio: '4:1', size: '1280 × 320 px', hint: 'Banner ancho encima del catálogo.' },
  home_feed: { ratio: '1:1', size: '800 × 800 px', hint: 'Tarjeta cuadrada entre productos.' },
  home_bottom: { ratio: '4:1', size: '1280 × 320 px', hint: 'Banner ancho debajo del listado.' },
  product_sidebar: { ratio: '1:1', size: '600 × 600 px', hint: 'Tarjeta en la ficha de producto.' },
};

export const AdBannerSchema = z.object({
  id: z.string().min(1),
  name: z.string().trim().min(1).max(120),
  /** `video` (4/11): MP4/WebM corto en bucle y mudo; `image_url` es su portada (obligatoria). */
  type: z.enum(['banner_image', 'html', 'video']),
  image_url: z.string().url().max(1000).or(z.literal('')),
  video_url: z.string().url().max(1000).or(z.literal('')).optional(),
  /** Se pinta en un iframe `sandbox` sin scripts (la 1.0 lo saneaba con DOMPurify). */
  html_content: z.string().max(5000),
  click_url: z.string().url().max(1000).startsWith('http').or(z.literal('')),
  /** Vacío = todos los países. */
  target_countries: z.array(CountryCodeSchema).max(40),
  placement: AdPlacementSchema,
  is_active: z.boolean(),
  starts_at: TimestampSchema.nullable(),
  ends_at: TimestampSchema.nullable(),
  impressions: z.number().int().min(0),
  clicks: z.number().int().min(0),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  created_by: UserIdSchema,
  /**
   * Solo en los banners que crea y quita el servidor (`pro_absolute`: el último producto activo de
   * un PRO Absoluto). El job nunca toca un banner sin esta marca; editarlo en el admin la conserva.
   */
  auto: z.enum(AD_AUTO_KINDS).optional(),
  /** Vendedor del banner automático. */
  seller_uid: UserIdSchema.optional(),
});
export type AdBanner = z.infer<typeof AdBannerSchema>;

/** `settings/adServer` (1.0 platform_config/ad_server; ids públicos, sin secretos). */
export const AdServerSettingsSchema = z.object({
  provider: z.enum(AD_PROVIDERS),
  google_ad_client_id: z.string().trim().max(40),
  google_ad_unit_id: z.string().trim().max(40),
  meta_placement_id: z.string().trim().max(80),
  /** Un anuncio cada N productos del feed (1.0 frecuencia_anuncios = 8). */
  feed_frequency: z.number().int().min(1).max(50),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type AdServerSettings = z.infer<typeof AdServerSettingsSchema>;

export const AD_SERVER_DEFAULTS = {
  provider: 'manual',
  google_ad_client_id: '',
  google_ad_unit_id: '',
  meta_placement_id: '',
  feed_frequency: 8,
} as const satisfies Omit<AdServerSettings, 'updated_at' | 'updated_by'>;

/** Activo, dentro de fechas y dirigido al país (1.0 filterActivePlacementAds). */
export function adIsLive(
  ad: Pick<AdBanner, 'is_active' | 'starts_at' | 'ends_at' | 'target_countries'>,
  now: number,
  country: string | null,
): boolean {
  if (!ad.is_active) return false;
  if (ad.starts_at && ad.starts_at.toMillis() > now) return false;
  if (ad.ends_at && ad.ends_at.toMillis() < now) return false;
  if (ad.target_countries.length > 0 && country) return ad.target_countries.includes(country);
  return true;
}

/** Rotación estable entre campañas del mismo placement (1.0 pickPlacementAd). */
export function pickPlacementAd<T>(ads: readonly T[], slotIndex = 0): T | null {
  if (ads.length === 0) return null;
  return ads[slotIndex % ads.length] ?? null;
}

/** Proveedor efectivo de un slot: híbrido alterna Google (pares) / manual (impares). */
export function resolveSlotProvider(provider: AdProvider, slotIndex: number): AdProvider {
  if (provider === 'hybrid') return slotIndex % 2 === 0 ? 'google' : 'manual';
  return provider;
}

export type FeedItem<T> = { type: 'product'; product: T } | { type: 'ad'; slotIndex: number };

/**
 * Intercala un slot de anuncio cada `frequency` productos (1.0 buildFeedItems). Con pocos
 * productos (p. ej. 2 y frecuencia 8) se añade un slot al final para que el feed tenga uno.
 */
export function buildFeedItems<T>(products: readonly T[], frequency: number): FeedItem<T>[] {
  if (products.length === 0) return [];
  const freq = Number.isFinite(frequency) && frequency >= 1 ? Math.floor(frequency) : 0;
  const items: FeedItem<T>[] = [];
  let slot = 0;
  products.forEach((product, index) => {
    items.push({ type: 'product', product });
    if (freq > 0 && (index + 1) % freq === 0) {
      items.push({ type: 'ad', slotIndex: slot });
      slot += 1;
    }
  });
  if (freq > 0 && slot === 0) items.push({ type: 'ad', slotIndex: 0 });
  return items;
}
